import React from "react";
import { Box, Typography } from "@mui/material";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import PlatformMappings from "./pages/PlatformMappings";
import Login from "./pages/Login";
import McpAuthorize from "./pages/mcp/McpAuthorize";
import UnauthenticatedRoute from "./routes/UnauthenticatedRoute";
import AuthenticatedRoute from "./routes/AuthenticatedRoute";
import useAuthListener from "./hooks/useAuth";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import NavigationProvider from "./providers/NavigationProvider";
import DocumentTitle from "./components/layout/DocumentTitle";
import { NavigationHistoryTracker } from "./hooks/useGoBack";
import useDefaults from "./hooks/useDefaults";
import useHandleError from "./hooks/useHandleError";
import Tracking from "./components/tracking/Tracking";
import Company from "./pages/company/Company";
import NewCompany from "./pages/company/NewCompany";
import EditCompany from "./pages/company/EditCompany";
import Campaigns from "./pages/Campaigns";
import NewCampaign from "./pages/campaign/NewCampaign";
import EditCampaign from "./pages/campaign/EditCampaign";
import ViewCampaign from "./pages/campaign/ViewCampaign";
import LegacyCampaignRedirect from "./routes/LegacyCampaignRedirect";
import LegacyUserRedirect from "./routes/LegacyUserRedirect";
import InternalCampaignDetails from "./pages/InternalCampaignDetails";
import InternalCampaignMargins from "./pages/InternalCampaignMargins";
import Metrics from "./pages/Metrics";
import Users from "./pages/users/Users";
import NewUser from "./pages/users/NewUser";
import EditUser from "./pages/users/EditUser";
import ViewCompany from "./pages/company/ViewCompany";
import Settings from "./pages/Settings";
import SystemHealth from "./pages/SystemHealth";
import { useLanguage } from "./hooks/useLanguage";
import { useAvatar } from "./hooks/useAvatar";
import { useGetMyUserQuery } from "./hooks/useGetUser";
import { useTokenRefresh } from "./hooks/useTokenRefresh";
import { useTokenValidation } from "./hooks/useTokenValidation";
import ViewUser from "./pages/users/ViewUser";
import MergedCampaignDetails from "./pages/MergedCampaignDetails";
import { useIdentifyUserHotjar } from "./hooks/useHotjar";
import CookieConsentBanner from "./components/cookie/CookieConsent";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import TermsAndConditions from "./pages/legal/TermsAndConditions";
import LegalNotice from "./pages/legal/LegalNotice";
import CookiePolicy from "./pages/legal/CookiePolicy";
import EditIONames from "./pages/insertion-orders/EditIONames";
import Processor from "./pages/Processor";
import Credentials from "./pages/Credentials";
import IOMargins from "./pages/insertion-orders/IOMargins";
import Api from "./pages/api/Api";
import Briefings from "./pages/Briefings";
import NewBriefing from "./pages/briefings/NewBriefing";
import EditBriefing from "./pages/briefings/EditBriefing";
import DataModel from "./pages/DataModel";
import ViewBriefing from "./pages/briefings/ViewBriefing";
import ViewAssociatedCampaign from "./pages/briefings/ViewAssociatedCampaign";
import EditAssociatedCampaign from "./pages/briefings/EditAssociatedCampaign";
import Wip from "./pages/Wip";
import DataHealth from "./pages/DataHealth";
import PurchaseOrders from "./pages/purchase-orders/PurchaseOrders";
import PurchaseOrderHistory from "./pages/purchase-orders/PurchaseOrderHistory";
import Invoices from "./pages/invoices/Invoices";
import InvoiceHistory from "./pages/invoices/InvoiceHistory";
import InvoiceDetail from "./pages/invoices/InvoiceDetail";
import ClientRegistry from "./pages/client-registry/ClientRegistry";
import CommercialDashboard from "./pages/commercial-dashboard/CommercialDashboard";
import CostAnalysis from "./pages/cost-analysis/CostAnalysis";
import ControlBoard from "./pages/control-board/ControlBoard";
import BusinessPlan from "./pages/control-board/BusinessPlan";
import CommercialProposals from "./pages/commercial-proposals/CommercialProposals";
import ServiceBriefings from "./pages/service-briefings/ServiceBriefings";
import ServiceBriefingForm from "./pages/service-briefings/ServiceBriefingForm";
import ViewServiceBriefing from "./pages/service-briefings/ViewServiceBriefing";
import NewCommercialProposal from "./pages/commercial-proposals/NewCommercialProposal";
import ViewCommercialProposal from "./pages/commercial-proposals/ViewCommercialProposal";

import OrbLoader from "./components/common/OrbLoader";
import DevIndicator from "./components/dev/DevIndicator";
import HomeViewClient from "./pages/client/HomeViewClient";
import CampaignViewClient from "./pages/client/CampaignViewClient";
import SubcampaignViewClient from "./pages/client/SubcampaignViewClient";
import NotFoundViewClient from "./pages/client/NotFoundViewClient";
import {
  ClientSwitch,
  ToParentCampaign,
} from "./routes/ClientRoutesViewClient";
// Separate component for authenticated app to avoid auth hooks running on legal pages
const AuthenticatedApp: React.FC = () => {
  useAuthListener();
  useDefaults();
  useHandleError();
  useLanguage();
  useAvatar();
  useGetMyUserQuery();
  useTokenRefresh();
  const tokenState = useTokenValidation(); // Proactively validate and refresh tokens on app startup
  useIdentifyUserHotjar();

  // Show loading screen ONLY when token is expired and being refreshed
  if (tokenState.isValidating && tokenState.isTokenExpired) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          gap: 2,
        }}
      >
        <OrbLoader size="xlarge" state="connecting" />
        <Typography variant="body1" color="text.secondary">
          Refreshing session...
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Tracking />
      <NavigationProvider />
      <DocumentTitle />
      <CookieConsentBanner />
      <Routes>
        {/* Unauthenticated routes */}
        <Route element={<UnauthenticatedRoute />}>
          <Route path="/login" element={<Login />} />
          {/*<Route path="/signup" element={<Signup />} />*/}
          <Route path="/forgot-password" element={<ForgotPassword />} />
        </Route>
        {/* Enlace de recuperación de contraseña: fuera de UnauthenticatedRoute para que
            funcione aunque en el navegador haya otra sesión abierta */}
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* Authenticated routes */}
        <Route element={<AuthenticatedRoute />}>
          {/* El inicio de cliente es el inicio de todos los roles */}
          <Route path="/" element={<HomeViewClient />} />
          {/* Campaña y subcampaña como las ve el cliente, también para los demás
              roles (se llega desde el inicio) */}
          <Route
            path="/client/campaigns/:campaignId"
            element={<CampaignViewClient />}
          />
          <Route
            path="/client/campaigns/:campaignId/subcampaigns/:subcampaignId"
            element={<SubcampaignViewClient />}
          />
          {/* Pantalla de "Conectar" de Claude con el MCP de Intelligence (OAuth) */}
          <Route path="/mcp/authorize" element={<McpAuthorize />} />
          <Route path="/companies" element={<Company />} />
          <Route path="/companies/:id" element={<ViewCompany />} />
          <Route path="/companies/new" element={<NewCompany />} />
          <Route path="/companies/:id/edit" element={<EditCompany />} />
          <Route path="/campaigns" element={<Campaigns />} />
          <Route path="/campaigns/new" element={<NewCampaign />} />
          <Route
            path="/campaigns/:campaignId"
            element={
              <ClientSwitch client={<CampaignViewClient />}>
                <ViewCampaign />
              </ClientSwitch>
            }
          />
          <Route
            path="/campaigns/:campaignId/subcampaigns/:subcampaignId"
            element={
              <ClientSwitch client={<SubcampaignViewClient />}>
                <ToParentCampaign />
              </ClientSwitch>
            }
          />
          <Route
            path="/campaigns/:campaignId/edit"
            element={<EditCampaign />}
          />
          <Route
            path="/campaigns/:campaignId/insertion-orders/edit-names"
            element={<EditIONames />}
          />
          {/* Legacy campaign paths, kept as redirects to the nested ones */}
          <Route
            path="/campaigns/view/:campaignId"
            element={<LegacyCampaignRedirect />}
          />
          <Route
            path="/campaigns/edit/:campaignId"
            element={<LegacyCampaignRedirect suffix="/edit" />}
          />
          <Route
            path="/internal-campaigns/:parentCampaignId/:internalCampaignId"
            element={<InternalCampaignDetails />}
          />
          <Route
            path="/campaign/:parentCampaignId/:mergedCampaignId"
            element={<MergedCampaignDetails />}
          />
          <Route path="/metrics" element={<Metrics />} />
          <Route
            path="/metrics/internal-metrics/:internalCampaignId"
            element={<InternalCampaignMargins />}
          />
          <Route
            path="/metrics/io-margins/:insertionOrderId"
            element={<IOMargins />}
          />
          <Route path="/users" element={<Users />} />
          <Route path="/users/new" element={<NewUser />} />
          <Route path="/users/:id" element={<ViewUser />} />
          <Route path="/users/:id/edit" element={<EditUser />} />
          {/* Legacy user paths, kept as redirects to the nested ones */}
          <Route path="/users/profile/:id" element={<LegacyUserRedirect />} />
          <Route
            path="/users/edit/:id"
            element={<LegacyUserRedirect suffix="/edit" />}
          />
          <Route path="/settings" element={<Settings />} />
          <Route path="/system-health" element={<SystemHealth />} />
          <Route path="/processor" element={<Processor />} />
          <Route path="/data-model" element={<DataModel />} />
          <Route path="/platform-mappings" element={<PlatformMappings />} />
          <Route path="/credentials" element={<Credentials />} />
          <Route path="/api" element={<Api />} />
          <Route path="/wip" element={<Wip />} />
          <Route path="/data-health" element={<DataHealth />} />
          <Route path="/purchase-orders" element={<PurchaseOrders />} />
          <Route
            path="/purchase-orders/history"
            element={<PurchaseOrderHistory />}
          />
          {/* Misma pantalla que la creación, precargada con la OC a editar */}
          <Route
            path="/purchase-orders/:id/edit"
            element={<PurchaseOrders />}
          />
          <Route path="/invoices" element={<Invoices />} />
          <Route path="/invoices/history" element={<InvoiceHistory />} />
          <Route path="/invoices/:id" element={<InvoiceDetail />} />
          {/* Registro de clientes: la fuente del numero que va dentro del numero
              de propuesta. */}
          <Route path="/client-registry" element={<ClientRegistry />} />
          {/* Dashboard comercial: pipeline de New Business (copia de Twenty) y
              KPIs de propuestas. */}
          <Route
            path="/commercial-dashboard"
            element={<CommercialDashboard />}
          />
          {/* Análisis de costes: coste real frente a venta por plataforma, tipo de
              campaña, objetivo, sector y tipo de cliente. */}
          <Route path="/cost-analysis" element={<CostAnalysis />} />
          <Route path="/control-board" element={<ControlBoard />} />
          <Route
            path="/control-board/business-plan"
            element={<BusinessPlan />}
          />
          {/* Propuesta comercial: cabecera mas tabla de proyectos. Sustituye al
              apartado de briefing comercial, que era de una campana por
              propuesta y se ha retirado de la navegacion. Sus endpoints y su
              tabla siguen en el backend: las propuestas HG26xxx ya emitidas no
              se han tocado. */}
          <Route
            path="/commercial-proposals"
            element={<CommercialProposals />}
          />
          <Route
            path="/commercial-proposals/new"
            element={<NewCommercialProposal />}
          />
          <Route
            path="/commercial-proposals/:id"
            element={<ViewCommercialProposal />}
          />
          {/* Briefings de House of Stories, House of AI y House of Digital. Alta y edición
              comparten pantalla, como las OC. */}
          <Route path="/service-briefings" element={<ServiceBriefings />} />
          <Route
            path="/service-briefings/new"
            element={<ServiceBriefingForm />}
          />
          <Route
            path="/service-briefings/:id"
            element={<ViewServiceBriefing />}
          />
          <Route
            path="/service-briefings/:id/edit"
            element={<ServiceBriefingForm />}
          />
          <Route path="/briefings" element={<Briefings />} />
          <Route path="/briefings/new" element={<NewBriefing />} />
          <Route path="/briefings/:id" element={<ViewBriefing />} />
          <Route path="/briefings/:id/edit" element={<EditBriefing />} />
          <Route
            path="/briefings/:id/associated-campaigns/:associatedId"
            element={<ViewAssociatedCampaign />}
          />
          <Route
            path="/briefings/:id/associated-campaigns/:associatedId/edit"
            element={<EditAssociatedCampaign />}
          />
          {/* Cualquier otra ruta: 404, con sidebar y tras el login */}
          <Route path="*" element={<NotFoundViewClient />} />
        </Route>
      </Routes>
    </>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <NavigationHistoryTracker />
      <DevIndicator />
      <Routes>
        {/* Public legal pages - completely separate from auth flow */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/legal-notice" element={<LegalNotice />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />

        {/* All other routes - wrapped in AuthenticatedApp with auth hooks */}
        <Route path="*" element={<AuthenticatedApp />} />
      </Routes>
    </Router>
  );
};

export default App;
